"use client";

import { useLocale, useTranslations } from "next-intl";
import { useMemo } from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";
import { SectionShell, EditorialTitle, GradientText, RevealOnScroll, useIsDark } from "./_primitives";

export default function RecurringChartSection() {
    const t = useTranslations();
    const locale = useLocale();
    const isDark = useIsDark();
    const fmtEuro = (v) => `${Math.round(v).toLocaleString(locale)} €`;
    const fmtEuroShort = (v) =>
        v >= 1000 ? `${Math.round(v / 1000)}k €` : `${v} €`;
    const axisColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(17,24,39,0.4)";
    const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.08)";
    const tooltipBg = isDark ? "#192734" : "#FFFFFF";
    const tooltipBorder = isDark ? "rgba(255,255,255,0.1)" : "rgba(17,24,39,0.1)";
    const tooltipLabel = isDark ? "rgba(255,255,255,0.7)" : "rgba(17,24,39,0.7)";
    const uniqueStroke = isDark ? "rgba(255,255,255,0.6)" : "rgba(17,24,39,0.5)";
    const uniqueFillStop = isDark ? "#FFFFFF" : "#111827";

    const data = useMemo(() => {
        // unique = single sale of 49€ at month 1, slowly decaying
        // recurring = 30 active subs growing to 200, paying 9.95€/mo
        const out = [];
        for (let m = 1; m <= 12; m++) {
            const subscribers = Math.round(20 + (m - 1) * 16); // 20 → 196
            const recurringMonth = subscribers * 9.95;
            const uniqueMonth = m === 1 ? 49 * 50 : 49 * Math.max(0, 12 - m);
            out.push({
                month: `M${m}`,
                recurring: out[m - 2]
                    ? out[m - 2].recurring + recurringMonth
                    : recurringMonth,
                unique: out[m - 2]
                    ? out[m - 2].unique + uniqueMonth
                    : uniqueMonth,
            });
        }
        return out;
    }, []);

    return (
        <SectionShell label={t("joinInstructor_recurring_marker")}>
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
                <RevealOnScroll>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                        {t("joinInstructor_recurring_eyebrow")}
                    </p>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinInstructor_recurring_title_part1")}
                        <br />
                        <GradientText>{t("joinInstructor_recurring_title_part2")}</GradientText>
                    </EditorialTitle>

                    <p className="mt-10 text-gray-600 dark:text-white/70 leading-relaxed text-base max-w-lg">
                        {t("joinInstructor_recurring_lead_part1")}{" "}
                        <span className="text-primary font-semibold">
                            {t("joinInstructor_recurring_lead_emphasis1")}
                        </span>{" "}
                        {t("joinInstructor_recurring_lead_part2")}{" "}
                        <span className="text-primary font-semibold">
                            {t("joinInstructor_recurring_lead_emphasis2")}
                        </span>
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4 mt-10">
                        <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-5">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-3">
                                {t("joinInstructor_recurring_card_classic_label")}
                            </div>
                            <p
                                className="text-gray-600 dark:text-white/70 leading-snug"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                {t("joinInstructor_recurring_card_classic_text")}
                            </p>
                        </div>
                        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3">
                                {t("joinInstructor_recurring_card_believemy_label")}
                            </div>
                            <p
                                className="text-gray-900 dark:text-white leading-snug"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                {t("joinInstructor_recurring_card_believemy_text")}
                            </p>
                        </div>
                    </div>
                </RevealOnScroll>

                <RevealOnScroll delay={0.15}>
                    <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-1">
                                    {t("joinInstructor_recurring_chart_eyebrow")}
                                </div>
                                <p
                                    className="text-base sm:text-xl text-gray-900 dark:text-white"
                                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                >
                                    {t("joinInstructor_recurring_chart_title")}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3 sm:gap-4 text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-white/55">
                                <span className="flex items-center gap-2">
                                    <span className="w-3 h-px bg-gray-400" />
                                    {t("joinInstructor_recurring_chart_legend_unique")}
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="w-3 h-px bg-primary" />
                                    {t("joinInstructor_recurring_chart_legend_recurring")}
                                </span>
                            </div>
                        </div>
                        <div className="h-[280px] sm:h-[340px] lg:h-[380px] w-full">
                            <ResponsiveContainer>
                                <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="grad-recurring" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#FF7A1A" stopOpacity={0.7} />
                                            <stop offset="100%" stopColor="#E51578" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="grad-unique" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={uniqueFillStop} stopOpacity={0.18} />
                                            <stop offset="100%" stopColor={uniqueFillStop} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke={gridColor} vertical={false} />
                                    <XAxis
                                        dataKey="month"
                                        stroke={axisColor}
                                        fontSize={10}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        stroke={axisColor}
                                        fontSize={10}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={fmtEuroShort}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: tooltipBg,
                                            border: `1px solid ${tooltipBorder}`,
                                            borderRadius: 12,
                                            fontSize: 12,
                                        }}
                                        labelStyle={{ color: tooltipLabel }}
                                        formatter={(v, key) => [
                                            fmtEuro(v),
                                            key === "recurring"
                                                ? t("joinInstructor_recurring_chart_legend_recurring")
                                                : t("joinInstructor_recurring_chart_legend_unique"),
                                        ]}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="unique"
                                        stroke={uniqueStroke}
                                        strokeWidth={1.5}
                                        fill="url(#grad-unique)"
                                        animationDuration={1400}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="recurring"
                                        stroke="#E8511D"
                                        strokeWidth={2.5}
                                        fill="url(#grad-recurring)"
                                        animationDuration={1800}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/55 text-center mt-4 font-mono">
                            {t("joinInstructor_recurring_chart_note")}
                        </p>
                    </div>
                </RevealOnScroll>
            </div>
        </SectionShell>
    );
}
